import { createContext } from 'react';

import type { Fulfillment, OrderData, OrderItemData } from '@shared/dream-components';

import type { CalComBooking } from '~/interfaces/cal_com';
import type { ReschedulingData } from '~/routes/subscribe/$subscriberId/_components/v2/hooks/useAccessFulfillment';

/**
 * Logic layer for the Orders tab — the `ManageOrdersElement` (the subscriber's orders + receipts,
 * fulfillment access, product reviews) and the Cal.com coaching-reschedule modal. Owns
 * `useStaticOrders` and `useAccessFulfillment`, the receipt fetcher + its pending state, and the
 * review-product navigation. Renders no UI; the colocated `OrdersSection` child renders the orders
 * list, the request-access teaser, and the reschedule modal from it.
 */
export type OrdersContextType = {
  orders: OrderData[];
  /** `write` access — gates the orders list vs the request-access teaser. */
  canManageOrders: boolean;
  isLoggedIn: boolean;
  /** Order whose receipt is mid-fetch — drives the per-row spinner. */
  pendingReceiptOrderId: string | null;
  subscriberEmail: string;
  subscriberFullName: string;
  publicationId: string;

  onViewReceipt: (orderId: string) => void;
  onClickReviewProduct: (orderId: string, orderItemId: string, productSlug: string) => void;
  onAccessFulfillment: (orderItem: OrderItemData, fulfillment: Fulfillment) => Promise<null>;

  // Cal.com coaching-reschedule modal — opened from a coaching-session order row.
  isReschedulingModalOpen: boolean;
  isRescheduling: boolean;
  reschedulingData: ReschedulingData | null;
  onCloseRescheduling: () => void;
  /** On reschedule success: persist the booking, refetch orders, toast. Curried by fulfillment. */
  onRescheduleBookingSuccess: (fulfillmentId: string) => (bookingData: CalComBooking) => Promise<void>;
};

export const OrdersContext = createContext<OrdersContextType | undefined>(undefined);
OrdersContext.displayName = 'OrdersContext';
